import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'
import FeedbackCard from './FeedbackCard'

function SupportResistancePracticeView({ onBack, onNavigate, onSmartBack, state }) {
  const smartBack = onSmartBack || (() => onNavigate('chapter_ch04'));
  // Mini canvas chart helper
  function SRPChart({ drawFn, h = 180 }) {
    const ref = useRef(null);
    useEffect(() => {
      const cv = ref.current; if (!cv) return;
      const ctx = cv.getContext('2d');
      drawFn(ctx, cv.width, cv.height);
    }, []); // eslint-disable-line
    return <canvas ref={ref} width={520} height={h} style={{ width: '100%', maxWidth: '520px', display: 'block', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.2)', marginBottom: '14px' }} />;
  }

  const drawBase = (ctx, W, H, prices, zones, markers) => {
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, W, H);
    const pad = { l: 20, r: 20, t: 22, b: 20 };
    const gW = W - pad.l - pad.r, gH = H - pad.t - pad.b;
    const N = prices.length;
    const px = i => pad.l + (i / (N - 1)) * gW;
    const py = v => pad.t + gH * (1 - v);
    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.03)'; ctx.lineWidth = 0.5;
    [0.25, 0.5, 0.75].forEach(f => { ctx.beginPath(); ctx.moveTo(pad.l, pad.t + gH * f); ctx.lineTo(pad.l + gW, pad.t + gH * f); ctx.stroke(); });
    // Zones
    (zones || []).forEach(({ lo, hi, color, dashed }) => {
      ctx.fillStyle = color + '18'; ctx.fillRect(pad.l, py(hi), gW, py(lo) - py(hi));
      ctx.strokeStyle = color + 'a0'; ctx.lineWidth = 1;
      ctx.setLineDash(dashed === 'bottom' ? [] : [5, 4]);
      const lineY = dashed === 'bottom' ? py(lo) : py(hi);
      ctx.beginPath(); ctx.moveTo(pad.l, lineY); ctx.lineTo(pad.l + gW, lineY); ctx.stroke();
      ctx.setLineDash([]);
      const zMid = (py(hi) + py(lo)) / 2;
      if (color.includes('7aab8a') || color === '#7aab8a') {
        ctx.font = '9px Jost,sans-serif'; ctx.fillStyle = color + '99'; ctx.textAlign = 'center';
        ctx.fillText('Support Zone', pad.l + gW / 2, zMid + 4);
      } else {
        ctx.font = '9px Jost,sans-serif'; ctx.fillStyle = color + '99'; ctx.textAlign = 'center';
        ctx.fillText('Resistance Zone', pad.l + gW / 2, zMid + 4);
      }
    });
    // Price line
    ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.8;
    ctx.beginPath();
    prices.forEach((v, i) => { i === 0 ? ctx.moveTo(px(i), py(v)) : ctx.lineTo(px(i), py(v)); });
    ctx.stroke();
    // Markers
    (markers || []).forEach(({ i: mi, color, label, dir }) => {
      const mx = px(mi), my = py(prices[mi]);
      ctx.beginPath(); ctx.arc(mx, my, 4, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill();
      if (label) {
        ctx.font = '9px Jost,sans-serif'; ctx.fillStyle = color; ctx.textAlign = 'center';
        ctx.fillText(label, mx, dir === 'up' ? my - 8 : my + 16);
      }
    });
    // Price axis labels
    ctx.font = '8px DM Mono,monospace'; ctx.fillStyle = 'rgba(255,255,255,0.22)'; ctx.textAlign = 'left';
  };

  // Scenario definitions
  const S = [
    {
      title: 'Identify the Support Zone',
      question: 'Where is the support zone on this chart?',
      options: ['At $195 — where price peaked','At $165 — where price bounced three times','At $180 — the current price','There is no support zone visible'],
      correct: 1,
      explanation: 'The support zone is at $165 — price tested this level three times and buyers stepped in each time. Three touches of the same level with bounces confirms strong demand. A CSP strike at or below $165 has the market\'s buying history behind it.',
      draw: (ctx, W, H) => {
        const prices = [0.92,0.87,0.82,0.76,0.69,0.61,0.53,0.46,0.41,0.37,0.35,0.34,0.35,0.40,0.47,0.55,0.62,0.67,0.64,0.59,0.53,0.47,0.42,0.38,0.35,0.34,0.35,0.40,0.46,0.53,0.59,0.62,0.59,0.54,0.49,0.43,0.37,0.35,0.34,0.37,0.44,0.53,0.62,0.71,0.78,0.84,0.88,0.91];
        drawBase(ctx, W, H, prices, [], []);
        // Price label at bounce level (no zone shading to test the student)
        const pad = { l: 20, r: 20, t: 22, b: 20 }, gH = H - pad.t - pad.b;
        ctx.font = '9px DM Mono,monospace'; ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.textAlign = 'left';
        ctx.fillText('$195', pad.l, pad.t + gH * 0.08 + 4);
        ctx.fillText('$180', pad.l, pad.t + gH * 0.36 + 4);
        ctx.fillText('$165', pad.l, pad.t + gH * 0.64 + 4);
      }
    },
    {
      title: 'Identify the Resistance Zone',
      question: 'Where is the resistance zone on this chart?',
      options: ['At $165 — the lowest point','At $185 — where price was rejected twice','At $175 — the midpoint','Resistance cannot be identified from this chart'],
      correct: 1,
      explanation: 'The resistance zone is at $185 — price tested this level twice and sellers pushed it back both times. This is a strong supply zone. A covered call at or just above $185 has the market\'s selling history behind it.',
      draw: (ctx, W, H) => {
        const prices = [0.12,0.19,0.26,0.34,0.42,0.49,0.55,0.60,0.64,0.67,0.70,0.68,0.64,0.59,0.53,0.47,0.43,0.40,0.43,0.48,0.53,0.57,0.61,0.65,0.68,0.71,0.69,0.65,0.60,0.54,0.48,0.43,0.38,0.33,0.28,0.24];
        drawBase(ctx, W, H, prices, [], []);
        const pad = { l: 20, r: 20, t: 22, b: 20 }, gH = H - pad.t - pad.b;
        ctx.font = '9px DM Mono,monospace'; ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.textAlign = 'left';
        ctx.fillText('$185', pad.l, pad.t + gH * 0.31 + 4);
        ctx.fillText('$175', pad.l, pad.t + gH * 0.55 + 4);
        ctx.fillText('$165', pad.l, pad.t + gH * 0.79 + 4);
      }
    },
    {
      title: 'CSP Strike Selection',
      question: 'Price is at $178 and trending upward. There is a confirmed support zone at $165–170 where price has bounced twice. You want to sell a CSP. Where should your strike be?',
      options: ['At $178 — at the current price','At $172 — just above the support zone','At $163 — just below the support zone','At $155 — far below support'],
      correct: 2,
      explanation: 'Your CSP strike should be AT or BELOW the support zone — not above it. Selling at $163 places your strike just below the confirmed support zone at $165–170. Price must break through an established demand area AND move below $163 before your put goes in the money. The support zone is your protection. Selling above support ($172) gives you no structural protection — any normal pullback to support immediately threatens your position. At or below support. Always.',
      draw: (ctx, W, H) => {
        const prices = [0.65,0.61,0.56,0.50,0.45,0.40,0.38,0.40,0.46,0.53,0.60,0.66,0.70,0.68,0.63,0.57,0.51,0.45,0.42,0.40,0.43,0.49,0.56,0.63,0.69,0.74,0.77];
        const zLo = 0.33, zHi = 0.52;
        drawBase(ctx, W, H, prices, [{ lo: zLo, hi: zHi, color: '#7aab8a' }], [
          { i: prices.length - 1, color: '#c9a84c', label: '$178', dir: 'up' },
        ]);
        const pad = { l: 20, r: 20, t: 22, b: 20 }, gH = H - pad.t - pad.b, gW = W - pad.l - pad.r;
        const py = v => pad.t + gH * (1 - v);
        // Bottom of zone dashed line ($165)
        ctx.strokeStyle = 'rgba(122,171,138,0.5)'; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(pad.l, py(zLo)); ctx.lineTo(pad.l + gW, py(zLo)); ctx.stroke();
        ctx.setLineDash([]);
        // Price labels
        ctx.font = '9px DM Mono,monospace'; ctx.textAlign = 'right';
        ctx.fillStyle = 'rgba(122,171,138,0.9)';
        ctx.fillText('$170', pad.l + gW - 2, py(zHi) - 3);
        ctx.fillText('$165', pad.l + gW - 2, py(zLo) - 3);
      }
    },
    {
      title: 'Covered Call Strike Selection',
      question: 'You own shares. Resistance is at $185. Where should your covered call strike be?',
      options: ['At $175 — below resistance','At $185 or just above — at the resistance zone','At $200 — far above resistance','At $165 — below current price'],
      correct: 1,
      explanation: 'Selling a covered call AT or just above resistance at $185 means price must break through an established supply zone to reach your strike. The resistance zone increases the probability your call expires worthless while you keep the premium.',
      draw: (ctx, W, H) => {
        const prices = [0.28,0.34,0.40,0.46,0.52,0.57,0.61,0.65,0.68,0.71,0.73,0.70,0.67,0.64,0.62,0.65,0.68,0.71,0.73,0.71,0.68,0.65,0.63,0.66,0.69,0.72,0.73,0.71,0.68,0.65,0.62];
        drawBase(ctx, W, H, prices, [{ lo: 0.66, hi: 0.78, color: '#c47a7a', dashed: 'bottom' }], [
          { i: 24, color: '#c47a7a', label: '$185 ✓', dir: 'up' },
        ]);
      }
    },
    {
      title: 'Support Becomes Resistance',
      question: 'Price broke below $175 and is now rallying back to test it. What has $175 become?',
      options: ['It remains support','Support has become resistance — former buyers want to exit at breakeven','The level has no significance after a break','It is now a neutral zone'],
      correct: 1,
      explanation: 'When price breaks below a support level, that level often becomes resistance. Former buyers who are now underwater want to exit at breakeven — creating selling pressure when price returns to that level. This is one of the most reliable patterns in technical analysis.',
      draw: (ctx, W, H) => {
        const prices = [0.82,0.78,0.74,0.70,0.65,0.60,0.55,0.52,0.50,0.48,0.51,0.54,0.57,0.60,0.63,0.65,0.64,0.61,0.58,0.54,0.50,0.45,0.40,0.36,0.32,0.29,0.27,0.30,0.35,0.41,0.47,0.52,0.57,0.61,0.64,0.63,0.60];
        const pad = { l: 20, r: 20, t: 22, b: 20 }, gW = W - pad.l - pad.r, gH = H - pad.t - pad.b;
        const py = v => pad.t + gH * (1 - v);
        const flipLevel = 0.62;
        drawBase(ctx, W, H, prices, [], []);
        // Show the S→R flip level
        ctx.strokeStyle = 'rgba(201,168,76,0.45)'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(pad.l, py(flipLevel)); ctx.lineTo(pad.l + gW, py(flipLevel)); ctx.stroke();
        ctx.setLineDash([]);
        ctx.font = '9px Jost,sans-serif'; ctx.fillStyle = 'rgba(201,168,76,0.7)'; ctx.textAlign = 'right';
        ctx.fillText('$175 — was support, now resistance', pad.l + gW - 4, py(flipLevel) - 5);
      }
    },
    {
      title: 'Combining: Structure + Momentum',
      question: 'Price is at the $165 support zone. RSI is at 32. Price has bounced here three times before. What do these signals combined suggest?',
      options: ['Avoid — too many signals to interpret','High conviction CSP setup — structural support confirmed by oversold RSI. Multiple signals aligned.','Sell a covered call here','Wait for price to break support before entering'],
      correct: 1,
      explanation: 'Three confirmations: structural support zone (price has defended this level three times), oversold RSI (momentum stretched bearish), and price at the zone right now. Three independent signals pointing to the same conclusion. A CSP at or below $165 has structure AND momentum confirming the setup.',
      draw: (ctx, W, H) => {
        const priceH = Math.round(H * 0.72);
        const rsiH = H - priceH - 4;
        const prices = [0.92,0.86,0.79,0.71,0.63,0.55,0.48,0.42,0.38,0.35,0.34,0.35,0.40,0.48,0.56,0.62,0.66,0.63,0.57,0.50,0.44,0.39,0.36,0.35,0.34,0.36,0.42];
        const rsiPts = [62,58,53,47,41,36,31,27,25,26,29,32,38,45,52,58,62,59,54,48,43,38,34,31,30,31,34];
        // Price chart (top portion)
        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, W, H);
        const pad = { l: 20, r: 20, t: 16, b: 4 };
        const gW = W - pad.l - pad.r, pgH = priceH - pad.t - pad.b;
        const px = i => pad.l + (i / (prices.length - 1)) * gW;
        const ppy = v => pad.t + pgH * (1 - v);
        // Support zone on price chart
        ctx.fillStyle = 'rgba(122,171,138,0.12)';
        ctx.fillRect(pad.l, ppy(0.42), gW, ppy(0.28) - ppy(0.42));
        ctx.strokeStyle = 'rgba(122,171,138,0.55)'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(pad.l, ppy(0.40)); ctx.lineTo(pad.l + gW, ppy(0.40)); ctx.stroke();
        ctx.setLineDash([]);
        // Price line
        ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.8; ctx.beginPath();
        prices.forEach((v, i) => { i === 0 ? ctx.moveTo(px(i), ppy(v)) : ctx.lineTo(px(i), ppy(v)); });
        ctx.stroke();
        ctx.font = '8px Jost,sans-serif'; ctx.fillStyle = 'rgba(122,171,138,0.6)'; ctx.textAlign = 'right';
        ctx.fillText('Support $165', pad.l + gW - 2, ppy(0.40) - 3);
        // RSI panel (bottom)
        const rY = priceH + 4;
        ctx.fillStyle = 'rgba(255,255,255,0.03)'; ctx.fillRect(pad.l, rY, gW, rsiH - 4);
        ctx.font = '8px DM Mono,monospace'; ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.textAlign = 'left';
        ctx.fillText('RSI', pad.l + 2, rY + 10);
        const rpy = v => rY + (rsiH - 8) * (1 - v / 100);
        // RSI 70/30 lines
        [70, 30].forEach(lv => {
          ctx.strokeStyle = lv === 70 ? 'rgba(196,122,122,0.4)' : 'rgba(122,171,138,0.4)';
          ctx.lineWidth = 0.5; ctx.setLineDash([3, 3]);
          ctx.beginPath(); ctx.moveTo(pad.l, rpy(lv)); ctx.lineTo(pad.l + gW, rpy(lv)); ctx.stroke();
          ctx.setLineDash([]);
        });
        // RSI line
        ctx.strokeStyle = '#9a8fc4'; ctx.lineWidth = 1.5; ctx.beginPath();
        rsiPts.forEach((v, i) => { const x = pad.l + (i / (rsiPts.length - 1)) * gW; i === 0 ? ctx.moveTo(x, rpy(v)) : ctx.lineTo(x, rpy(v)); });
        ctx.stroke();
        // Current RSI dot
        const lastX = pad.l + gW, lastY = rpy(rsiPts[rsiPts.length - 1]);
        ctx.beginPath(); ctx.arc(lastX, lastY, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#7aab8a'; ctx.fill();
        ctx.font = '8px DM Mono,monospace'; ctx.fillStyle = 'rgba(122,171,138,0.8)'; ctx.textAlign = 'right';
        ctx.fillText('RSI 32', pad.l + gW - 2, lastY - 4);
      }
    },
  ];

  const [scIdx, setScIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [allDone, setAllDone] = useState(false);
  const sc = S[scIdx];
  const isCorrect = selected === sc.correct;

  const handleAnswer = (idx) => {
    if (selected !== null) return;
    const correct = idx === sc.correct;
    setSelected(idx);
    setScore(s => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
    try { localStorage.setItem('tlaw_support_resistance_score', JSON.stringify({ correct: score.correct + (correct ? 1 : 0), total: score.total + 1, date: new Date().toISOString() })); } catch {}
  };
  const handleNext = () => {
    if (scIdx + 1 >= S.length) { setAllDone(true); return; }
    setScIdx(i => i + 1); setSelected(null);
  };
  const reset = () => { setScIdx(0); setSelected(null); setScore({ correct: 0, total: 0 }); setAllDone(false); };

  if (allDone) {
    const pct = Math.round((score.correct / score.total) * 100);
    const scoreColor = score.correct >= 5 ? V.sage : score.correct >= 4 ? V.gold : V.rose;
    const scoreMsg = score.correct === 6 ? 'You can read structure. You know where the levels are.' : score.correct >= 4 ? 'Strong. Review the scenarios you missed.' : 'Review Chapter 4 and try again.';
    return (
      <div style={{ padding: '40px 32px', maxWidth: '680px' }}>
        <div style={{ display: 'inline-block', padding: '4px 12px', background: 'rgba(201,168,76,0.08)', border: `0.5px solid ${V.border}`, borderRadius: '20px', fontSize: '10px', color: V.gold, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", marginBottom: '14px' }}>Support &amp; Resistance — Practice</div>
        <h1 style={{ ...serif, fontSize: '34px', fontWeight: 400, color: V.gold, margin: '0 0 24px' }}>Session Complete</h1>
        <div style={{ ...mono, fontSize: '52px', color: scoreColor, marginBottom: '8px' }}>{score.correct} / {score.total}</div>
        <p style={{ fontSize: '14px', color: scoreColor, fontFamily: "'Jost',sans-serif", marginBottom: '24px' }}>{scoreMsg}</p>
        <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '18px 22px', marginBottom: '24px' }}>
          <p style={{ ...serif, fontSize: '15px', fontStyle: 'italic', color: V.muted, lineHeight: 1.8, margin: 0 }}>
            Structure is not drawn on charts — it is revealed by price behavior. The more you read charts, the more clearly the zones speak to you.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button style={{ ...btnGold, fontSize: '13px', padding: '10px 20px' }} onClick={reset}>Practice Again →</button>
          <button style={{ ...btnGhost, fontSize: '13px', padding: '10px 18px' }} onClick={smartBack}>← Back to Chapter</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 32px', maxWidth: '680px' }}>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
        <button style={{ ...btnGhost, fontSize: '12px' }} onClick={smartBack}>← Back to Chapter</button>
        <button style={{ ...btnGhost, fontSize: '12px' }} onClick={onBack}>Dashboard</button>
      </div>
      <div style={{ display: 'inline-block', padding: '4px 12px', background: 'rgba(201,168,76,0.08)', border: `0.5px solid ${V.border}`, borderRadius: '20px', fontSize: '10px', color: V.gold, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", marginBottom: '14px' }}>Support &amp; Resistance — Practice</div>
      <h1 style={{ ...serif, fontSize: '32px', fontWeight: 400, color: V.gold, margin: '0 0 4px' }}>◈ Support &amp; Resistance</h1>
      <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '24px' }}>Can you find the zones?</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {S.map((_, i) => (
            <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i < scIdx ? V.sage : i === scIdx ? V.gold : V.border, transition: 'background 0.3s' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: '14px' }}>
          <span style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>Scenario {scIdx + 1} of {S.length}</span>
          <span style={{ fontSize: '11px', color: V.gold, fontFamily: "'DM Mono',monospace" }}>Score: {score.correct} / {score.total}</span>
        </div>
      </div>
      <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '20px', marginBottom: '14px' }}>
        <div style={{ fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: V.gold, fontFamily: "'Jost',sans-serif", marginBottom: '8px' }}>Scenario {scIdx + 1} — {sc.title}</div>
        <SRPChart drawFn={sc.draw} />
        <p style={{ fontSize: '14px', color: 'rgba(240,237,230,0.88)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0 }}>{sc.question}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
        {sc.options.map((opt, i) => {
          const isSelected = selected === i;
          const correct = i === sc.correct;
          let bg = 'transparent', border = V.border, color = V.muted;
          if (selected !== null) {
            if (correct) { bg = 'rgba(122,171,138,0.12)'; border = 'rgba(122,171,138,0.4)'; color = V.sage; }
            else if (isSelected) { bg = 'rgba(196,122,122,0.1)'; border = 'rgba(196,122,122,0.3)'; color = V.rose; }
          } else if (isSelected) {
            bg = 'rgba(201,168,76,0.1)'; border = 'rgba(201,168,76,0.4)'; color = V.goldL;
          }
          return (
            <button key={i} style={{ textAlign: 'left', padding: '12px 16px', background: bg, border: `0.5px solid ${border}`, borderRadius: '8px', color, fontSize: '13px', fontFamily: "'Jost',sans-serif", cursor: selected !== null ? 'default' : 'pointer', lineHeight: 1.6 }}
              onClick={() => handleAnswer(i)}>
              {selected !== null && correct && <span style={{ color: V.sage, marginRight: '8px' }}>✓</span>}
              {selected !== null && isSelected && !correct && <span style={{ color: V.rose, marginRight: '8px' }}>✗</span>}
              {opt}
            </button>
          );
        })}
      </div>
      {selected !== null && (
        <div style={{ background: isCorrect ? 'rgba(122,171,138,0.07)' : 'rgba(196,122,122,0.07)', border: `0.5px solid ${isCorrect ? 'rgba(122,171,138,0.25)' : 'rgba(196,122,122,0.25)'}`, borderRadius: '10px', padding: '16px 18px', marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: isCorrect ? V.sage : V.rose, fontFamily: "'Jost',sans-serif", marginBottom: '6px' }}>{isCorrect ? '✓ Correct' : '✗ Not quite'}</div>
          <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.75, margin: 0 }}>{sc.explanation}</p>
        </div>
      )}
      {selected !== null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <span style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>{score.correct} correct of {score.total} answered</span>
          <button style={{ ...btnGold, padding: '10px 24px', fontSize: '13px' }} onClick={handleNext}>
            {scIdx + 1 < S.length ? 'Next Scenario →' : 'See Results →'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Confluence Practice chart helper ─────────────────────────────
function drawConfluenceScene(ctx, W, H, { prices, ma20, ma50, zones, rsi, markers, noZoneLabel }) {
  const prH = Math.round(H * 0.64);
  const rH = H - prH - 4;
  ctx.clearRect(0, 0, W, H); ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, W, H);
  const p = { l: 20, r: 20, t: 18, b: 2 };
  const gW = W - p.l - p.r, pgH = prH - p.t - p.b;
  const N = prices.length;
  const px = i => p.l + (i / (N - 1)) * gW;
  const py = v => p.t + pgH * (1 - v);
  // Subtle grid
  ctx.strokeStyle = 'rgba(255,255,255,0.03)'; ctx.lineWidth = 0.5;
  [0.25, 0.5, 0.75].forEach(f => { ctx.beginPath(); ctx.moveTo(p.l, p.t + pgH * f); ctx.lineTo(p.l + gW, p.t + pgH * f); ctx.stroke(); });
  // Zones
  (zones || []).forEach(({ lo, hi, col, lbl }) => {
    ctx.fillStyle = col + '14'; ctx.fillRect(p.l, py(hi), gW, py(lo) - py(hi));
    ctx.strokeStyle = col + '60'; ctx.lineWidth = 0.8; ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(p.l, py(hi)); ctx.lineTo(p.l + gW, py(hi)); ctx.stroke();
    ctx.setLineDash([]);
    if (!noZoneLabel) {
      ctx.font = '8px Jost,sans-serif'; ctx.fillStyle = col + '80'; ctx.textAlign = 'center';
      ctx.fillText(lbl || (col.includes('7aab8a') || col === '#7aab8a' ? 'Support Zone' : 'Resistance Zone'), p.l + gW / 2, (py(hi) + py(lo)) / 2 + 4);
    }
  });
  // 50-day MA (sage)
  if (ma50) {
    ctx.strokeStyle = 'rgba(122,171,138,0.55)'; ctx.lineWidth = 1.2; ctx.beginPath();
    let first50 = true;
    ma50.forEach((v, i) => { if (v == null) return; if (first50) { ctx.moveTo(px(i), py(v)); first50 = false; } else ctx.lineTo(px(i), py(v)); });
    ctx.stroke();
    const last50 = ma50.filter(v => v != null).slice(-1)[0];
    const last50i = ma50.map((v, i) => v != null ? i : -1).filter(i => i >= 0).slice(-1)[0];
    if (last50 != null) { ctx.font = '7px Jost,sans-serif'; ctx.fillStyle = 'rgba(122,171,138,0.6)'; ctx.textAlign = 'left'; ctx.fillText('50MA', px(last50i) - 22, py(last50) - 3); }
  }
  // 20-day MA (sky)
  if (ma20) {
    ctx.strokeStyle = 'rgba(122,158,196,0.7)'; ctx.lineWidth = 1.2; ctx.beginPath();
    let first20 = true;
    ma20.forEach((v, i) => { if (v == null) return; if (first20) { ctx.moveTo(px(i), py(v)); first20 = false; } else ctx.lineTo(px(i), py(v)); });
    ctx.stroke();
    const last20 = ma20.filter(v => v != null).slice(-1)[0];
    const last20i = ma20.map((v, i) => v != null ? i : -1).filter(i => i >= 0).slice(-1)[0];
    if (last20 != null) { ctx.font = '7px Jost,sans-serif'; ctx.fillStyle = 'rgba(122,158,196,0.6)'; ctx.textAlign = 'left'; ctx.fillText('20MA', px(last20i) - 22, py(last20) - 3); }
  }
  // Price line
  ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.8; ctx.beginPath();
  prices.forEach((v, i) => { i === 0 ? ctx.moveTo(px(i), py(v)) : ctx.lineTo(px(i), py(v)); });
  ctx.stroke();
  ctx.beginPath(); ctx.arc(px(N - 1), py(prices[N - 1]), 3, 0, Math.PI * 2); ctx.fillStyle = '#c9a84c'; ctx.fill();
  // Price level markers
  (markers || []).forEach(({ v, lbl, col }) => {
    ctx.strokeStyle = (col || 'rgba(255,255,255,0.15)'); ctx.lineWidth = 0.5; ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(p.l, py(v)); ctx.lineTo(p.l + gW * 0.55, py(v)); ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = '8px DM Mono,monospace'; ctx.fillStyle = col || 'rgba(255,255,255,0.35)'; ctx.textAlign = 'left';
    ctx.fillText(lbl, p.l + 2, py(v) - 3);
  });
  // RSI panel
  if (rsi) {
    const rY = prH + 4;
    ctx.fillStyle = 'rgba(255,255,255,0.015)'; ctx.fillRect(p.l, rY, gW, rH - 4);
    ctx.font = '7px DM Mono,monospace'; ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.textAlign = 'left';
    ctx.fillText('RSI', p.l + 2, rY + 9);
    const rN = rsi.length;
    const rpx = i => p.l + (i / (rN - 1)) * gW;
    const rpy = v => rY + 2 + (rH - 10) * (1 - v / 100);
    [[70, '#c47a7a'], [30, '#7aab8a']].forEach(([lv, col]) => {
      ctx.strokeStyle = col + '45'; ctx.lineWidth = 0.5; ctx.setLineDash([3, 2]);
      ctx.beginPath(); ctx.moveTo(p.l, rpy(lv)); ctx.lineTo(p.l + gW, rpy(lv)); ctx.stroke();
      ctx.setLineDash([]);
      ctx.font = '7px DM Mono,monospace'; ctx.fillStyle = col + '80'; ctx.textAlign = 'right';
      ctx.fillText(String(lv), p.l + gW - 2, rpy(lv) - 2);
    });
    ctx.strokeStyle = '#9a8fc4'; ctx.lineWidth = 1.4; ctx.beginPath();
    rsi.forEach((v, i) => { i === 0 ? ctx.moveTo(rpx(i), rpy(v)) : ctx.lineTo(rpx(i), rpy(v)); });
    ctx.stroke();
    const lastRSI = rsi[rsi.length - 1];
    ctx.beginPath(); ctx.arc(rpx(rN - 1), rpy(lastRSI), 3, 0, Math.PI * 2);
    ctx.fillStyle = lastRSI >= 70 ? '#c47a7a' : lastRSI <= 30 ? '#7aab8a' : '#9a8fc4'; ctx.fill();
    ctx.font = '8px DM Mono,monospace'; ctx.fillStyle = 'rgba(255,255,255,0.55)'; ctx.textAlign = 'right';
    ctx.fillText('RSI ' + lastRSI, p.l + gW - 6, rpy(lastRSI) - 4);
  }
}

export { drawConfluenceScene }
export default SupportResistancePracticeView
